import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import TextInput from 'components/Inputs/TextInput/TextInput';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorList from 'components/ErrorList';
import Debounce from 'utils/Debounce';
import store from 'store/Store';
import {
    Box, Grid, Button, Text
} from 'components/ui';
import ErrorsUtils from 'utils/Errors';
import GiftCardExpandButton from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard/GiftCardExpandButton/GiftCardExpandButton';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { handleGiftCardNumberKeyDown, validateGiftCard, validateGiftCardPin } from 'utils/validateGiftCardInputs';
import formatGiftCardNumber from 'utils/formatGiftCardNumber';
import removeSpaces from 'utils/removeSpaces';

const giftCardNumberLength = FormValidator.FIELD_LENGTHS.giftCardNumber;
const giftCardPinLength = FormValidator.FIELD_LENGTHS.giftCardPin;

class UseAGiftCard extends BaseClass {
    state = {
        showForm: false
    };

    notifyFormVisibility = isVisible => {
        this.props.onFormVisibilityChange && this.props.onFormVisibilityChange(isVisible);
    };

    onClick = () => {
        FrictionlessCheckoutBindings.setGiftCardPaymentAnalytics();
        this.setState(
            {
                showForm: true
            },
            () => this.notifyFormVisibility(true)
        );
    };

    validateGiftCardForm = () => {
        ErrorsUtils.clearErrors();
        const fieldsForValidation = [this.giftCardNumberInput, this.giftCardPINInput];
        ErrorsUtils.collectClientFieldErrors(fieldsForValidation);
        ErrorsUtils.validate();
        let totalErrors = store.getState().errors;
        totalErrors = Object.assign({}, totalErrors[ErrorConstants.ERROR_LEVEL.FIELD]);
        const hasErrors = Object.keys(totalErrors).length;

        return hasErrors;
    };

    handleCancelClick = () => {
        this.clearForm();
        this.setState(
            {
                showForm: false,
                errorMessages: []
            },
            () => this.notifyFormVisibility(false)
        );
    };

    clearForm = () => {
        this.setState({
            giftCardNumber: '',
            giftCardPin: ''
        });
    };

    giftCardApplied = () => {
        this.clearForm();
        this.setState({ showForm: false, errorMessages: [] }, () => this.notifyFormVisibility(false));
    };

    componentWillUnmount() {
        this.notifyFormVisibility(false);
    }

    addGiftCard = e => {
        e.preventDefault();

        if (!this.validateGiftCardForm()) {
            const giftCard = {
                gcNumber: removeSpaces(this.state.giftCardNumber),
                gcPin: this.state.giftCardPin
            };
            this.props.applyGiftCard(giftCard, this.giftCardApplied).catch(err => {
                ErrorsUtils.collectAndValidateBackEndErrors(err, this);
                this.setState({ errorMessages: err.errorMessages });
            });
        }
    };

    handleOnChange = e => {
        const { name, value } = e.target;

        // Format gift card number for display, keep other fields as-is
        const formattedValue = name === 'giftCardNumber' ? formatGiftCardNumber(value) : value;

        this.setState({
            [name]: formattedValue
        });
    };

    handleGiftCardNumberPaste = e => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');

        // Remove all non-numeric characters and format
        const numericOnly = pastedText.replace(/\D/g, '');

        // Limit to 16 digits
        const limitedDigits = numericOnly.substring(0, 16);

        // Format and update state
        const formattedValue = formatGiftCardNumber(limitedDigits);

        this.setState({
            giftCardNumber: formattedValue
        });
    };

    addGiftCardDebounce = Debounce.preventDoubleClick(this.addGiftCard);

    renderGiftCardForm = () => {
        const { localization, withoutLeftMargin = false, hasTitle = true } = this.props;

        return (
            <>
                <Box
                    is='form'
                    noValidate
                    id='giftcard_form'
                    action=''
                    autoComplete='off'
                    marginTop={[4, 3]}
                    marginLeft={withoutLeftMargin ? 0 : [3, 5]}
                    marginRight={3}
                    maxWidth={640}
                    onSubmit={this.addGiftCardDebounce}
                >
                    <ErrorList errorMessages={this.state.errorMessages} />
                    {hasTitle && (
                        <Text
                            is='h3'
                            fontWeight='bold'
                            marginBottom={3}
                            children={localization.giftCard}
                        />
                    )}
                    <Grid
                        gap={3}
                        columns={1}
                        maxWidth={['100%', '380px']}
                    >
                        <TextInput
                            marginBottom={null}
                            autoOff={true}
                            hideAsterisk
                            name='giftCardNumber'
                            label={localization.giftCardNumberPlaceholder}
                            required={true}
                            type='tel'
                            value={this.state.giftCardNumber}
                            onChange={this.handleOnChange}
                            ref={comp => (this.giftCardNumberInput = comp)}
                            validateError={giftCardNumber => validateGiftCard(giftCardNumber, false)}
                            onKeyDown={handleGiftCardNumberKeyDown}
                            onPaste={this.handleGiftCardNumberPaste}
                            maxLength={giftCardNumberLength}
                            data-at={Sephora.debug.dataAt('gc_card_input')}
                        />
                        <TextInput
                            marginBottom={null}
                            autoOff={true}
                            hideAsterisk
                            name='giftCardPin'
                            label={localization.pinNumberPlaceholder}
                            required={true}
                            type='tel'
                            value={this.state.giftCardPin}
                            onChange={this.handleOnChange}
                            ref={comp => (this.giftCardPINInput = comp)}
                            validateError={giftCardPin => validateGiftCardPin(giftCardPin, false)}
                            onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                            onPaste={FormValidator.pasteAcceptOnlyNumbers}
                            maxLength={giftCardPinLength}
                            data-at={Sephora.debug.dataAt('pin_input')}
                        />
                    </Grid>
                    <Grid
                        gap={3}
                        columns={2}
                        marginTop={4}
                        gridTemplateColumns={['repeat(2, 1fr)', 'repeat(2, 0fr)']}
                    >
                        <Button
                            variant='secondary'
                            type='button'
                            block={true}
                            children={localization.cancelBtn}
                            onClick={this.handleCancelClick}
                            data-at={Sephora.debug.dataAt('gc_cancel_btn')}
                            width={['100%', '184px']}
                        />
                        <Button
                            variant='primary'
                            type='submit'
                            block={true}
                            ref={comp => (this.applyButton = comp)}
                            children={localization.applyBtn}
                            data-at={Sephora.debug.dataAt('gc_apply_btn')}
                            width={['100%', '184px']}
                        />
                    </Grid>
                </Box>
            </>
        );
    };

    render() {
        const { showForm } = this.state;
        const { localization, isEditMode, isNewUserPaymentView } = this.props;

        return (
            <>
                {!showForm ? (
                    <GiftCardExpandButton
                        useGiftCard={localization.useGiftCard}
                        onClick={this.onClick}
                        isEditMode={isEditMode}
                        isNewUserPaymentView={isNewUserPaymentView}
                    />
                ) : (
                    this.renderGiftCardForm()
                )}
            </>
        );
    }
}

export default wrapComponent(UseAGiftCard, 'UseAGiftCard', true);
